import {
  BlowerDwelling,
  CatalogPickerSlot,
  ComponentCatalogEntry,
  HaveTile,
  PickerCatalogEntry,
  PickerIdForSlot,
  TileFuelLiteral
} from './component-catalog-types';
import {
  BlowerComponentId,
  ComponentPickerOption,
  FurnaceFuel,
  FurnaceFuelOption,
  PackagedHeatingId
} from './system-composition-types';

export const COMPONENT_CATALOG: readonly ComponentCatalogEntry[] = [
  {
    code: 'AC',
    haveSection: 'Outdoor',
    pickerSlot: 'outdoor',
    pickerId: 'ac',
    pickerRank: 0,
    label: 'Air Conditioner',
    hiddenFromOffer: false
  },
  {
    code: 'HP',
    haveSection: 'Outdoor',
    pickerSlot: 'outdoor',
    pickerId: 'hp',
    pickerRank: 1,
    label: 'Heat Pump',
    hiddenFromOffer: false
  },
  {
    code: 'AN',
    haveSection: 'Outdoor',
    pickerSlot: 'outdoor',
    pickerId: 'next-gen-ac',
    pickerRank: 2,
    label: 'Next-Gen Air Conditioner',
    hiddenFromOffer: true
  },
  {
    code: 'SC',
    haveSection: 'Outdoor',
    pickerSlot: 'outdoor',
    pickerId: 'next-gen-hp',
    pickerRank: 3,
    label: 'Next-Gen Heat Pump',
    hiddenFromOffer: true
  },
  {
    code: 'AH',
    haveSection: 'Indoor',
    pickerSlot: 'blower',
    pickerId: 'air-handler',
    pickerRank: 2,
    label: 'Air Handler',
    hiddenFromOffer: false
  },
  {
    code: 'DH',
    haveSection: 'Indoor',
    pickerSlot: 'blower',
    pickerId: 'next-gen-air-handler',
    pickerRank: 3,
    label: 'Next-Gen Air Handler',
    hiddenFromOffer: true
  },
  {
    code: 'EC',
    haveSection: 'Indoor',
    pickerSlot: 'coil',
    pickerId: 'coil',
    pickerRank: 0,
    label: 'Evaporator Coil',
    hiddenFromOffer: false
  },
  {
    code: 'EN',
    haveSection: 'Indoor',
    pickerSlot: 'coil',
    pickerId: 'next-gen-coil',
    pickerRank: 1,
    label: 'Next-Gen Evaporator Coil',
    hiddenFromOffer: true
  },
  {
    code: 'UF',
    haveSection: 'Blower',
    pickerSlot: 'blower',
    pickerId: 'furnace',
    pickerRank: 0,
    label: 'Furnace',
    fuels: ['NG', 'LP', 'Oil'],
    fuelLabelStyle: 'fuel-then-name',
    hiddenFromOffer: false
  },
  {
    code: 'MF',
    haveSection: 'Blower',
    pickerSlot: 'blower',
    pickerId: 'mobile-home-furnace',
    pickerRank: 1,
    label: 'Mobile-Home Furnace',
    fuels: ['NG', 'LP', 'Oil', 'Electric'],
    fuelLabelStyle: 'name-then-fuel',
    hiddenFromOffer: false
  },
  {
    code: 'MB',
    haveSection: 'Blower',
    pickerSlot: 'blower',
    pickerId: 'modular-blower',
    pickerRank: 4,
    label: 'Modular Blower',
    hiddenFromOffer: false
  },
  {
    code: 'PA',
    haveSection: 'Packaged',
    pickerSlot: 'packaged-cooling',
    pickerId: 'packaged-ac',
    pickerRank: 0,
    label: 'Air Conditioner',
    haveLabel: 'Packaged AC',
    hiddenFromOffer: false
  },
  {
    code: 'PH',
    haveSection: 'Packaged',
    pickerSlot: 'packaged-cooling',
    pickerId: 'packaged-hp',
    pickerRank: 1,
    label: 'Heat Pump',
    haveLabel: 'Packaged Heat Pump',
    hiddenFromOffer: false
  },
  {
    code: 'PG',
    haveSection: 'Packaged',
    pickerSlot: null,
    label: 'Gas Pack (AC + Furnace)',
    hiddenFromOffer: false
  },
  {
    code: 'PD',
    haveSection: 'Packaged',
    pickerSlot: null,
    label: 'Dual Fuel (HP + Furnace)',
    hiddenFromOffer: false
  },
  {
    code: 'PT',
    haveSection: 'Packaged',
    pickerSlot: null,
    label: 'Packaged Terminal (PTAC/PTHP)',
    hiddenFromOffer: false
  }
];

export const PACKAGED_HEATING_PICKER_OPTIONS: ComponentPickerOption<PackagedHeatingId>[] =
  [{ id: 'packaged-furnace', label: 'Furnace', hiddenFromOffer: false }];

export const CATALOG_FUELS: readonly {
  tile: TileFuelLiteral;
  picker: FurnaceFuel;
  label: string;
}[] = [
  { tile: 'NG', picker: 'ng', label: 'Natural Gas' },
  { tile: 'LP', picker: 'lp', label: 'Propane' },
  { tile: 'Oil', picker: 'oil', label: 'Oil' },
  { tile: 'Electric', picker: 'electric', label: 'Electric' }
];

export const FURNACE_FUEL_OPTIONS: FurnaceFuelOption[] = CATALOG_FUELS.map(
  (fuel) => ({ value: fuel.picker, label: fuel.label })
);

export const DWELLING_EXCLUDED_BLOWER_CODE: Record<
  BlowerDwelling,
  'UF' | 'MF' | null
> = {
  mobile: 'UF',
  modular: null,
  standard: 'MF'
};

const haveFuelLabel = (
  entry: ComponentCatalogEntry,
  fuel: TileFuelLiteral
): string =>
  entry.fuelLabelStyle === 'fuel-then-name'
    ? `${fuel} ${entry.label}`
    : `${entry.label} (${fuel})`;

const buildHaveTiles = (): HaveTile[] => {
  const tiles: HaveTile[] = [];
  const seenCodes = new Set<string>();
  for (const entry of COMPONENT_CATALOG) {
    if (entry.fuels) {
      for (const fuel of entry.fuels) {
        tiles.push({
          id: `${entry.code}:${fuel}`,
          code: entry.code,
          fuel,
          label: haveFuelLabel(entry, fuel),
          section: entry.haveSection,
          hiddenFromOffer: entry.hiddenFromOffer
        });
      }
    } else if (!seenCodes.has(entry.code)) {
      seenCodes.add(entry.code);
      tiles.push({
        id: entry.code,
        code: entry.code,
        fuel: null,
        label: entry.haveLabel ?? entry.label,
        section: entry.haveSection,
        hiddenFromOffer: entry.hiddenFromOffer
      });
    }
  }
  return tiles;
};

export const HAVE_TILES: HaveTile[] = buildHaveTiles();

export const FUEL_TILE_CODES = new Set(
  COMPONENT_CATALOG.filter(
    (entry) => entry.fuels && entry.fuels.length > 0
  ).map((entry) => entry.code)
);

type EntryForSlot<S extends CatalogPickerSlot> = Extract<
  PickerCatalogEntry,
  { pickerSlot: S }
>;

const isPickerEntryForSlot =
  (slot: CatalogPickerSlot) =>
  (entry: ComponentCatalogEntry): entry is PickerCatalogEntry =>
    entry.pickerSlot === slot;

function pickerOptionsForSlot<S extends CatalogPickerSlot>(
  slot: S
): ComponentPickerOption<PickerIdForSlot[S]>[];
function pickerOptionsForSlot(
  slot: CatalogPickerSlot
): ComponentPickerOption<PickerIdForSlot[CatalogPickerSlot]>[] {
  const seen = new Set<string>();
  return COMPONENT_CATALOG.filter(isPickerEntryForSlot(slot))
    .sort((a, b) => a.pickerRank - b.pickerRank)
    .flatMap((entry) => {
      if (seen.has(entry.pickerId)) {
        return [];
      }
      seen.add(entry.pickerId);
      return [
        {
          id: entry.pickerId,
          label: entry.label,
          hiddenFromOffer: entry.hiddenFromOffer
        }
      ];
    });
}

export const OUTDOOR_PICKER_OPTIONS = pickerOptionsForSlot('outdoor');
export const BLOWER_PICKER_OPTIONS = pickerOptionsForSlot('blower');
export const COIL_PICKER_OPTIONS = pickerOptionsForSlot('coil');
export const PACKAGED_COOLING_PICKER_OPTIONS =
  pickerOptionsForSlot('packaged-cooling');

const isBlowerEntry = (
  entry: ComponentCatalogEntry
): entry is EntryForSlot<'blower'> => entry.pickerSlot === 'blower';

export const blowerPickerIdForCode = (
  code: string
): BlowerComponentId | null => {
  const entry = COMPONENT_CATALOG.find(
    (item) => item.code === code && isBlowerEntry(item)
  );
  return entry && isBlowerEntry(entry) ? entry.pickerId : null;
};

const isElectricFuel = (fuel: FurnaceFuel | TileFuelLiteral | null): boolean =>
  fuel === 'electric' || fuel === 'Electric';

export const excludedBlowerCodeForDwelling = (
  dwelling: BlowerDwelling,
  selectedFuel: FurnaceFuel | TileFuelLiteral | null = null
): string | null => {
  const excluded = DWELLING_EXCLUDED_BLOWER_CODE[dwelling];
  if (excluded === 'MF' && isElectricFuel(selectedFuel)) {
    return null;
  }
  return excluded;
};

export const excludedBlowerPickerIdForDwelling = (
  dwelling: BlowerDwelling,
  selectedFuel: FurnaceFuel | null = null
): BlowerComponentId | null => {
  const excludedCode = excludedBlowerCodeForDwelling(dwelling, selectedFuel);
  return excludedCode === null ? null : blowerPickerIdForCode(excludedCode);
};
